import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ClipboardList, Radio, MonitorSmartphone, Users,
  BarChart3, FileText, Settings, Bell, Activity, LogOut, Menu, X, ShieldCheck, Thermometer, AlertTriangle,
} from "lucide-react";

// Sidebar sections — exactly as approved for Phase D.
const NAV = [
  { section: "BUILD", items: [
    { to: "/supervisor/build-orders", label: "Build Orders",  icon: ClipboardList },
    { to: "/supervisor/live-monitor", label: "Live Monitor",  icon: Radio },
    { to: "/supervisor/stations",     label: "Stations",      icon: MonitorSmartphone },
    { to: "/supervisor/technicians",  label: "Technicians",   icon: Users },
    { to: "/supervisor/component-issues", label: "Component Issues", icon: AlertTriangle, openCountBadge: true },
  ]},
  { section: "ANALYTICS", items: [
    { to: "/supervisor/reports", label: "Reports", icon: BarChart3 },
    { to: "/supervisor/build-reports", label: "Build Reports", icon: FileText },
  ]},
  { section: "SYSTEM", items: [
    { to: "/supervisor/settings/testing-standards", label: "Testing Standards", icon: Thermometer },
    { to: "/supervisor/settings",      label: "Settings",      icon: Settings },
    { to: "/supervisor/notifications", label: "Notifications", icon: Bell },
    { to: "/supervisor/activity-log",  label: "Activity Log",  icon: Activity },
  ]},
];

export function SupervisorLayout({ children, hideMobileHeader = false }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [openIssueCount, setOpenIssueCount] = useState(0);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const m = await import("@/lib/api");
        const { data } = await m.default.get("/admin/component-issues/open-count");
        if (alive) setOpenIssueCount(data.open || 0);
      } catch {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="supervisor-shell min-h-screen flex text-white overflow-x-hidden" style={{ background: "#050507" }}>
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <aside
        className={`fixed lg:static z-50 top-0 bottom-0 left-0 w-[260px] shrink-0 border-r flex flex-col transition-transform lg:pointer-events-auto ${open ? "translate-x-0 pointer-events-auto" : "-translate-x-full lg:translate-x-0 pointer-events-none"}`}
        style={{ background: "#0a0a0d", borderColor: "rgba(255,255,255,0.06)" }}
        data-testid="supervisor-sidebar"
      >
        <div className="px-5 py-5 flex items-center gap-3 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <img src="/idea-logo.png" alt="IDEA" className="h-7 w-auto" style={{ objectFit: "contain" }} />
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.28em] font-semibold" style={{ color: "var(--accent)" }}>SUPERVISOR</div>
          </div>
          <button onClick={() => setOpen(false)} className="ml-auto lg:hidden text-white/60" aria-label="Close menu"><X size={18}/></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5" data-testid="supervisor-nav">
          {NAV.map((group) => (
            <div key={group.section}>
              <div className="px-3 mb-2 text-[10px] tracking-[0.28em] text-white/35 font-semibold">{group.section}</div>
              <div className="space-y-0.5">
                {group.items.map((it) => {
                  const active = loc.pathname === it.to || (it.to === "/supervisor/build-orders" && loc.pathname === "/supervisor");
                  const Icon = it.icon;
                  const cls = `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors ${active ? "text-white" : "text-white/60 hover:text-white/90"}`;
                  const style = active ? { background: "rgba(0,229,255,0.10)", boxShadow: "inset 2px 0 0 var(--accent)" } : {};
                  return (
                    <Link
                      key={it.label}
                      to={it.to}
                      className={cls}
                      style={style}
                      data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      <Icon size={15} className="shrink-0" style={active ? { color: "var(--accent)" } : {}} />
                      <span className="truncate flex-1">{it.label}</span>
                      {it.openCountBadge && openIssueCount > 0 && (
                        <span
                          className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(255,152,0,0.18)", color: "#ffb857" }}
                          data-testid="sidebar-open-issue-badge"
                        >{openIssueCount}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-3 rounded-lg p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(0,229,255,0.12)", color: "var(--accent)" }}><ShieldCheck size={16}/></div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold truncate">Supervisor</div>
              <div className="text-[10px] tracking-[0.18em] text-white/45 font-medium">Administrator Access</div>
            </div>
            <button onClick={doLogout} className="text-white/50 hover:text-white" title="Sign out" aria-label="Sign out" data-testid="supervisor-logout"><LogOut size={15}/></button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {!hideMobileHeader && (
          <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b" style={{ background: "#0a0a0d", borderColor: "rgba(255,255,255,0.06)" }}>
            <button onClick={() => setOpen(true)} className="text-white/70" aria-label="Open menu" data-testid="supervisor-menu-open"><Menu size={20}/></button>
            <img src="/idea-logo.png" alt="IDEA" className="h-5 w-auto" style={{ objectFit: "contain" }} />
            <div className="w-5" />
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
